/**
 * RebateRulesContainer - Business Logic Container for Rule Editing
 *
 * This container manages all business logic, state, and API calls related to
 * editing rebate rules. It follows the Container/Presenter pattern:
 * - Container (this file): Business logic, validation, API calls, state management
 * - Presenter (RebateRulesTab): Pure UI rendering and user interactions
 *
 * Responsibilities:
 * - Edit mode state management
 * - Rule validation (business rules + form validation)
 * - Delta analysis orchestration
 * - Save flow and API calls
 * - Rule CRUD operations
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import RebateRulesTab from './RebateRulesTab';
import { DeltaWarningDialog } from 'src/components/rebates/DeltaWarningDialog';
import {
  useAnalyzeDelta,
  useApplyChanges,
  type PendingChange,
} from 'src/api/rebates/useRebateAgreementEdit';
import { ProductGroup } from 'src/api/rebates/useProductGroups';
import {
  DeltaAnalysisResult,
  DivisionSimpleDTO,
  RebateRule,
} from '@erp/shared-types';
import {
  CoreEnumOptionAssociation,
  useBulkEnumOptionAssociations,
} from 'src/api/core/enumOptionAssociations/useEnumOptionAssociations';
import { useRebateRuleTagMutations } from 'src/api/rebates/useRebateRuleTagMutations';
import useCoreEnums from 'src/api/core/core-enums/useCoreEnums';
import {
  useManualAmountsByAgreement,
  ManualAmount,
} from 'src/api/rebates/useManualAmounts';
import type { RebateLineItem } from 'src/api/rebates/useRebateLineItems';

interface RebateRulesContainerProps {
  agreementId: string;
  rebateRules: RebateRule[] | undefined;
  rebateRulesLoading: boolean;
  divisions: DivisionSimpleDTO[];
  agreementStatus: 'draft' | 'finalized';
  productGroups?: ProductGroup[];
  onSaveComplete?: () => void; // Callback when save succeeds
  onRulesWithIssuesChange?: (count: number) => void; // Callback when validation count changes
  invoices?: Array<{
    id: string;
    files?: {
      transformedFile?: { id: string; name: string } | null;
    };
  }>;
  onDownloadDocument?: (documentId: string, fileName?: string) => void;
  rebateTotalsByRule?: Map<string, number>; // Total rebate amount per rule ID
  tierStatusByRule?: Map<
    string,
    { estimates?: number; predictions?: number; reported?: number }
  >; // Tier status per rule by type
  volumesByRule?: Map<
    string,
    {
      qualifyingVolume: number;
      affectedVolume: number;
      discountedQualifyingVolume: number;
      discountedAffectedVolume: number;
      discountPercentage: number;
    }
  >; // Volume totals per rule (gross and discounted)
  // Agreement dates for rule duration context
  agreementStartDate?: string;
  agreementEndDate?: string;
  /** Line items for proportional split calculations in manual amounts */
  lineItems?: RebateLineItem[];
}

export const RebateRulesContainer: React.FC<RebateRulesContainerProps> = ({
  agreementId,
  rebateRules,
  rebateRulesLoading,
  divisions,
  agreementStatus,
  productGroups,
  onSaveComplete,
  onRulesWithIssuesChange,
  invoices,
  onDownloadDocument,
  rebateTotalsByRule,
  tierStatusByRule,
  volumesByRule,
  agreementStartDate,
  agreementEndDate,
  lineItems,
}) => {
  // ========================================
  // STATE MANAGEMENT
  // ========================================
  const [isEditMode, setIsEditMode] = useState(false);
  const [localRules, setLocalRules] = useState<RebateRule[]>([]);
  const [originalRules, setOriginalRules] = useState<RebateRule[]>([]);
  const [deletedRuleIds, setDeletedRuleIds] = useState<Set<string>>(new Set());
  const [ruleValidationStatus, setRuleValidationStatus] = useState<
    Map<string, boolean>
  >(new Map());
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());

  // Delta analysis state
  const [isDeltaDialogOpen, setIsDeltaDialogOpen] = useState(false);
  const [deltaAnalysis, setDeltaAnalysis] =
    useState<DeltaAnalysisResult | null>(null);

  // ========================================
  // API HOOKS
  // ========================================
  const analyzeDeltaMutation = useAnalyzeDelta(agreementId);
  const applyChangesMutation = useApplyChanges(agreementId);

  // Bulk fetch all rule tags
  const ruleIds = useMemo(() => {
    if (!rebateRules || rebateRules.length === 0) return [];
    return rebateRules.map((rule) => rule.id);
  }, [rebateRules]);

  const entities = useMemo(() => {
    if (ruleIds.length === 0) return undefined;
    return ruleIds.map((ruleId) => ({
      entityType: 'rebate_rule',
      entityId: ruleId,
    }));
  }, [ruleIds]);

  const { data: ruleAssociations, isLoading: tagsLoading } =
    useBulkEnumOptionAssociations(entities, !!entities && entities.length > 0);

  // Bulk fetch all manual amounts for the agreement (instead of per-rule)
  const { data: allManualAmounts, isLoading: manualAmountsLoading } =
    useManualAmountsByAgreement(
      agreementId,
      undefined,
      undefined,
      !!agreementId,
    );

  // Create manual amounts map by rule ID
  const manualAmountsByRuleId = useMemo(() => {
    const map = new Map<string, ManualAmount[]>();
    if (allManualAmounts) {
      allManualAmounts.forEach((amount) => {
        const existing = map.get(amount.ruleId) || [];
        map.set(amount.ruleId, [...existing, amount]);
      });
    }
    return map;
  }, [allManualAmounts]);

  // Fetch enum definition for available tags
  const { data: rebateRuleTagsEnum } = useCoreEnums({
    name: 'Rebate Rule Tags',
    includeOptions: true,
  });

  const availableTags = useMemo(() => {
    return rebateRuleTagsEnum?.edges[0]?.options || [];
  }, [rebateRuleTagsEnum?.edges]);

  // Create tags map by rule ID
  const tagsByRuleId = useMemo(() => {
    const map = new Map<string, CoreEnumOptionAssociation[]>();
    if (ruleAssociations && ruleIds) {
      ruleIds.forEach((ruleId) => {
        const ruleTags = ruleAssociations.filter(
          (assoc) =>
            assoc.entityType === 'rebate_rule' && assoc.entityId === ruleId,
        );
        map.set(ruleId, ruleTags);
      });
    }
    return map;
  }, [ruleAssociations, ruleIds]);

  // ========================================
  // VALIDATION LOGIC (Centralized)
  // ========================================
  const getRuleValidationErrors = useCallback((rule: RebateRule): string[] => {
    if (rule.isManual) return []; // Manual rules have different validation

    const errors: string[] = [];

    // Check 1: Growth rules must have growthBaseline
    if (
      rule.thresholdType === 'growth' &&
      (!rule.growthBaseline || rule.growthBaseline === '0.00')
    ) {
      errors.push('Missing growth baseline');
    }

    // Check 2: Growth/volume rules must have at least one tier
    if (
      (rule.thresholdType === 'growth' || rule.thresholdType === 'volume') &&
      (!rule.tiers || rule.tiers.length === 0)
    ) {
      errors.push('No tiers defined');
    }

    // Check 3: Growth/volume rules must have at least one division
    if (
      (rule.thresholdType === 'growth' || rule.thresholdType === 'volume') &&
      (!rule.divisions || rule.divisions.length === 0)
    ) {
      errors.push('No applicable divisions');
    }

    return errors;
  }, []);

  // Check if any rules have validation errors
  const hasValidationErrors = useMemo(() => {
    if (!isEditMode) return false;

    return localRules.some((rule) => {
      const isValid = ruleValidationStatus.get(rule.id);
      return isValid !== true; // undefined or false means invalid
    });
  }, [isEditMode, localRules, ruleValidationStatus]);

  // Count rules with validation issues (works for both edit and non-edit mode)
  const rulesWithIssuesCount = useMemo(() => {
    if (agreementStatus !== 'draft') {
      return 0;
    }

    // In edit mode, check localRules (including form validation status)
    if (isEditMode && localRules && localRules.length > 0) {
      return localRules.filter((rule) => {
        // Check both business rule errors and form validation status
        const businessRuleErrors = getRuleValidationErrors(rule);
        const formValidationStatus = ruleValidationStatus.get(rule.id);

        // Rule has issues if it has business rule errors OR form validation failed
        return businessRuleErrors.length > 0 || formValidationStatus === false;
      }).length;
    }

    // In non-edit mode, check rebateRules (saved rules)
    if (!rebateRules || rebateRules.length === 0) {
      return 0;
    }

    return rebateRules.filter((rule) => {
      const errors = getRuleValidationErrors(rule);
      return errors.length > 0;
    }).length;
  }, [
    isEditMode,
    localRules,
    rebateRules,
    agreementStatus,
    getRuleValidationErrors,
    ruleValidationStatus,
  ]);

  // Notify parent of validation count changes
  useEffect(() => {
    onRulesWithIssuesChange?.(rulesWithIssuesCount);
  }, [rulesWithIssuesCount, onRulesWithIssuesChange]);

  // ========================================
  // EDIT MODE HANDLERS
  // ========================================
  const handleEnterEditMode = useCallback(() => {
    if (!rebateRules) return;

    // Clone current rules into local state
    setLocalRules(JSON.parse(JSON.stringify(rebateRules)));
    setOriginalRules(JSON.parse(JSON.stringify(rebateRules)));
    setDeletedRuleIds(new Set());

    // Initialize validation status for all existing rules as valid
    const initialValidationStatus = new Map<string, boolean>();
    rebateRules.forEach((rule) => {
      initialValidationStatus.set(rule.id, true);
    });
    setRuleValidationStatus(initialValidationStatus);

    setIsEditMode(true);
  }, [rebateRules]);

  const handleCancelEditMode = useCallback(() => {
    setIsEditMode(false);
    setLocalRules([]);
    setOriginalRules([]);
    setDeletedRuleIds(new Set());
    setRuleValidationStatus(new Map());
  }, []);

  const handleDiscardChanges = useCallback(() => {
    // Reset local rules to original state without exiting edit mode
    setLocalRules(JSON.parse(JSON.stringify(originalRules)));
    setDeletedRuleIds(new Set());

    // Re-initialize validation status
    const initialValidationStatus = new Map<string, boolean>();
    originalRules.forEach((rule) => {
      initialValidationStatus.set(rule.id, true);
    });
    setRuleValidationStatus(initialValidationStatus);
  }, [originalRules]);

  // ========================================
  // PENDING CHANGES COMPUTATION
  // ========================================
  const computePendingChanges = useCallback(
    (
      local: RebateRule[],
      original: RebateRule[],
      deleted: Set<string>,
    ): PendingChange[] => {
      const pendingChanges: PendingChange[] = [];

      // Normalize sortOrder based on final array position before comparison.
      // This ensures clean sequential ordering (0, 1, 2, ...) after save,
      // regardless of add/delete operations during editing.
      const normalizedLocal = local.map((rule, index) => ({
        ...rule,
        sortOrder: index,
      }));

      // Find deletions - only for rules that exist in the database (non-temp IDs)
      deleted.forEach((ruleId) => {
        // Skip temp IDs - they were never saved to the database
        if (ruleId.startsWith('temp-')) {
          return;
        }
        pendingChanges.push({
          type: 'delete_rule',
          entityId: ruleId,
        });
      });

      // Find additions and edits (using normalizedLocal for correct sortOrder)
      for (const localRule of normalizedLocal) {
        if (localRule.id.startsWith('temp-')) {
          // New rule - strip temp ID before sending
          const { id, ...ruleData } = localRule;
          pendingChanges.push({
            type: 'add_rule',
            data: ruleData,
          });
        } else {
          // Existing rule - check if modified
          const origRule = original.find((r) => r.id === localRule.id);
          if (
            origRule &&
            JSON.stringify(origRule) !== JSON.stringify(localRule)
          ) {
            // For edit_rule, send the entire rule data including tiers, divisions, product groups
            // The backend will handle updating nested relationships
            const { id, rebateAgreementId, createdAt, updatedAt, ...ruleData } =
              localRule;

            pendingChanges.push({
              type: 'edit_rule',
              entityId: localRule.id,
              data: ruleData,
            });
          }
        }
      }

      return pendingChanges;
    },
    [],
  );

  // ========================================
  // DELTA ANALYSIS & SAVE LOGIC
  // ========================================
  const handleSaveComplete = useCallback(() => {
    // Exit edit mode and clean up state
    setIsEditMode(false);
    setLocalRules([]);
    setOriginalRules([]);
    setDeletedRuleIds(new Set());
    setIsDeltaDialogOpen(false);
    setDeltaAnalysis(null);

    // Notify parent to refetch data
    onSaveComplete?.();
  }, [onSaveComplete]);

  const handleSaveChanges = useCallback(async () => {
    const pendingChanges = computePendingChanges(
      localRules,
      originalRules,
      deletedRuleIds,
    );

    if (pendingChanges.length === 0) {
      setIsEditMode(false);
      return;
    }

    try {
      // Analyze delta before saving
      const result = await analyzeDeltaMutation.mutateAsync({
        pendingChanges,
      });

      console.log('[DEBUG] Delta analysis result:', result.data);

      // If severity is "none", skip dialog and apply changes immediately
      if (result.data.severity === 'none') {
        console.log(
          '[DEBUG] Severity is "none", skipping dialog and applying changes immediately',
        );

        await applyChangesMutation.mutateAsync({
          pendingChanges,
          acknowledged: false,
        });

        handleSaveComplete();
        return;
      }

      // For minor/major/destructive, show the dialog
      setDeltaAnalysis(result.data);
      setIsDeltaDialogOpen(true);
    } catch (error) {
      console.error('Failed to analyze delta:', error);
    }
  }, [
    localRules,
    originalRules,
    deletedRuleIds,
    computePendingChanges,
    analyzeDeltaMutation,
    applyChangesMutation,
    handleSaveComplete,
  ]);

  const handleConfirmDelta = useCallback(async () => {
    const pendingChanges = computePendingChanges(
      localRules,
      originalRules,
      deletedRuleIds,
    );

    try {
      await applyChangesMutation.mutateAsync({
        pendingChanges,
        acknowledged: deltaAnalysis?.severity === 'destructive',
      });

      handleSaveComplete();
    } catch (error) {
      console.error('Failed to apply changes:', error);
    }
  }, [
    localRules,
    originalRules,
    deletedRuleIds,
    computePendingChanges,
    applyChangesMutation,
    deltaAnalysis,
    handleSaveComplete,
  ]);

  const handleCloseDeltaDialog = useCallback(() => {
    setIsDeltaDialogOpen(false);
    setDeltaAnalysis(null);
  }, []);

  // ========================================
  // RULE OPERATIONS
  // ========================================
  const handleCreateRuleInEditMode = useCallback(() => {
    const newRuleId = `temp-${Date.now()}`;
    const newRule: RebateRule = {
      id: newRuleId,
      rebateAgreementId: agreementId,
      name: 'New Rule',
      thresholdType: 'volume',
      isManual: false,
      // Sensible default: one tier with 0% qualifier -> 5% rebate
      tiers: [
        {
          id: `temp-tier-${Date.now()}`,
          tierOrder: 1,
          thresholdValue: '0',
          rebateValue: '5',
        },
      ],
      // Sensible default: all divisions selected
      divisions: divisions.map((d) => ({
        id: d.id,
        name: d.name,
      })),
      // Sensible default: exclusive with no product groups (= all products)
      qualifyingProductGroups: [],
      affectedProductGroups: [],
      qualifyingInclusionType: 'exclusive',
      affectedInclusionType: 'exclusive',
      sortOrder: localRules.length, // Placeholder - recalculated on save based on final array position
      growthBaseline: null,
      discountPercentage: '0',
      isRetroactiveToDollarOne: false,
      notes: '',
      startDate: null, // Default to null (use agreement dates)
      endDate: null, // Default to null (use agreement dates)
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    setLocalRules((prev) => [...prev, newRule]);
    setRuleValidationStatus((prev) => {
      const newMap = new Map(prev);
      newMap.set(newRuleId, true); // New rules with sensible defaults start as valid
      return newMap;
    });

    // Auto-expand the new rule
    setExpandedRules((prev) => new Set(prev).add(newRuleId));
  }, [agreementId, localRules.length, divisions]);

  const handleDeleteRuleInEditMode = useCallback((rule: RebateRule) => {
    // Mark as deleted and remove from local rules
    setDeletedRuleIds((prev) => new Set(prev).add(rule.id));
    setLocalRules((prev) => prev.filter((r) => r.id !== rule.id));
  }, []);

  const handleDuplicateRuleInEditMode = useCallback(
    (rule: RebateRule) => {
      const newRuleId = `temp-${Date.now()}`;
      const duplicatedRule: RebateRule = {
        ...JSON.parse(JSON.stringify(rule)), // Deep clone
        id: newRuleId,
        name: `${rule.name} (Copy)`,
        sortOrder: localRules.length,
        createdAt: new Date(),
        updatedAt: new Date(),
        // Generate new IDs for tiers to avoid conflicts
        tiers: rule.tiers?.map((tier, index) => ({
          ...tier,
          id: `temp-tier-${Date.now()}-${index}`,
        })),
      };

      setLocalRules((prev) => [...prev, duplicatedRule]);
      setRuleValidationStatus((prev) => {
        const newMap = new Map(prev);
        newMap.set(newRuleId, true);
        return newMap;
      });

      // Auto-expand the duplicated rule
      setExpandedRules((prev) => new Set(prev).add(newRuleId));
    },
    [localRules.length],
  );

  const handleUpdateRuleInEditMode = useCallback(
    (ruleId: string, updates: Partial<RebateRule>) => {
      setLocalRules((prev) =>
        prev.map((r) => (r.id === ruleId ? { ...r, ...updates } : r)),
      );
    },
    [],
  );

  const handleRuleValidationChange = useCallback(
    (ruleId: string, isValid: boolean) => {
      setRuleValidationStatus((prev) => {
        const newMap = new Map(prev);
        newMap.set(ruleId, isValid);
        return newMap;
      });
    },
    [],
  );

  const handleToggleRuleExpansion = useCallback((ruleId: string) => {
    setExpandedRules((prev) => {
      const next = new Set(prev);
      if (next.has(ruleId)) {
        next.delete(ruleId);
      } else {
        next.add(ruleId);
      }
      return next;
    });
  }, []);

  const handleReorderRules = useCallback((newOrder: RebateRule[]) => {
    // Update local rules with new order
    setLocalRules(newOrder);
  }, []);

  // ========================================
  // TAG OPERATIONS
  // ========================================
  const {
    handleRuleTagAdd: baseHandleRuleTagAdd,
    handleRuleTagRemove: baseHandleRuleTagRemove,
    tagsUpdatingByRuleId,
  } = useRebateRuleTagMutations();

  // Wrap handlers to pass current tags from tagsByRuleId
  const handleRuleTagAdd = useCallback(
    (ruleId: string, tagId: string) => {
      const currentTags = tagsByRuleId.get(ruleId) || [];
      baseHandleRuleTagAdd(ruleId, tagId, currentTags);
    },
    [tagsByRuleId, baseHandleRuleTagAdd],
  );

  const handleRuleTagRemove = useCallback(
    (ruleId: string, tagId: string) => {
      const currentTags = tagsByRuleId.get(ruleId) || [];
      baseHandleRuleTagRemove(ruleId, tagId, currentTags);
    },
    [tagsByRuleId, baseHandleRuleTagRemove],
  );

  // ========================================
  // COMPUTE DIRTY STATE
  // ========================================
  const isDirty = useMemo(() => {
    if (!isEditMode) return false;
    if (localRules.length !== originalRules.length) return true;
    if (deletedRuleIds.size > 0) return true;
    return JSON.stringify(localRules) !== JSON.stringify(originalRules);
  }, [isEditMode, localRules, originalRules, deletedRuleIds]);

  // ========================================
  // RENDER PRESENTER
  // ========================================
  return (
    <>
      <RebateRulesTab
        // Data props
        agreementId={agreementId}
        rebateRules={isEditMode ? localRules : rebateRules}
        rebateRulesLoading={rebateRulesLoading}
        divisions={divisions}
        agreementStatus={agreementStatus}
        productGroups={productGroups}
        // Edit mode state
        isEditMode={isEditMode}
        isDirty={isDirty}
        hasValidationErrors={hasValidationErrors}
        isSaving={
          applyChangesMutation.isPending || analyzeDeltaMutation.isPending
        }
        // Expansion state
        expandedRules={expandedRules}
        onToggleRuleExpansion={handleToggleRuleExpansion}
        // Edit mode handlers
        onEnterEditMode={handleEnterEditMode}
        onCancelEditMode={handleCancelEditMode}
        onDiscardChanges={handleDiscardChanges}
        onSaveChanges={handleSaveChanges}
        // Rule operations
        onCreateRule={isEditMode ? handleCreateRuleInEditMode : undefined}
        onUpdateRule={isEditMode ? handleUpdateRuleInEditMode : undefined}
        onDeleteRule={isEditMode ? handleDeleteRuleInEditMode : undefined}
        onDuplicateRule={isEditMode ? handleDuplicateRuleInEditMode : undefined}
        onValidationChange={isEditMode ? handleRuleValidationChange : undefined}
        onReorderRules={handleReorderRules}
        // Validation
        ruleValidationStatus={ruleValidationStatus}
        getRuleValidationErrors={getRuleValidationErrors}
        // Deleted rules tracking
        deletedRuleIds={deletedRuleIds}
        originalRules={originalRules}
        // Tags data (bulk fetched)
        tagsByRuleId={tagsByRuleId}
        availableTags={availableTags}
        tagsLoading={tagsLoading}
        onRuleTagAdd={handleRuleTagAdd}
        onRuleTagRemove={handleRuleTagRemove}
        tagsUpdatingByRuleId={tagsUpdatingByRuleId}
        // Manual amounts data (bulk fetched)
        manualAmountsByRuleId={manualAmountsByRuleId}
        manualAmountsLoading={manualAmountsLoading}
        // Rebate totals per rule
        rebateTotalsByRule={rebateTotalsByRule}
        tierStatusByRule={tierStatusByRule}
        volumesByRule={volumesByRule}
        // Agreement dates for rule duration context
        agreementStartDate={agreementStartDate}
        agreementEndDate={agreementEndDate}
        // Line items for proportional split in manual amounts
        lineItems={lineItems}
      />

      {/* Delta Warning Dialog */}
      <DeltaWarningDialog
        open={isDeltaDialogOpen}
        onClose={handleCloseDeltaDialog}
        onConfirm={handleConfirmDelta}
        analysis={deltaAnalysis}
        isLoading={applyChangesMutation.isPending}
        invoices={invoices}
        onDownloadDocument={onDownloadDocument}
      />
    </>
  );
};

export default RebateRulesContainer;
